import React from "react";
import { cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const buttonClasses = cva(
  "inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:scale-95",
  {
    variants: {
      variant: {
        primary: "focus:ring-blue-500",
        secondary:
          "bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500",
        outline:
          "border-2 bg-transparent hover:bg-opacity-10 focus:ring-blue-500",
      },
      size: {
        small: "text-sm px-3 py-1.5",
        medium: "text-base px-4 py-2",
        large: "text-lg px-6 py-3",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "medium",
    },
  }
);

const Button = ({
  // Required parameters with defaults
  text = "Read More",
  text_font_size = "24px",
  text_font_family = "Sora",
  text_font_weight = "700",
  text_line_height = "31px",
  text_text_align = "center",
  text_color = "#1c233a",
  border_border_radius = "24px",
  border_border = "1px solid #1c233a",

  // Optional parameters (no defaults)
  fill_background_color,
  layout_width,
  padding,
  position,
  margin,

  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = "button",
  ...props
}) => {
  // Safe validation for optional parameters
  const hasValidBackgroundColor =
    fill_background_color &&
    typeof fill_background_color === "string" &&
    fill_background_color?.trim() !== "";
  const hasValidWidth =
    layout_width &&
    typeof layout_width === "string" &&
    layout_width?.trim() !== "";
  const hasValidPadding =
    padding && typeof padding === "string" && padding?.trim() !== "";
  const hasValidMargin =
    margin && typeof margin === "string" && margin?.trim() !== "";
  const hasValidPosition =
    position && typeof position === "string" && position?.trim() !== "";

  // Build optional Tailwind classes
  const optionalClasses = [
    hasValidWidth ? `w-[${layout_width}]` : "",
    hasValidPadding ? `p-[${padding}]` : "",
    hasValidMargin ? `m-[${margin}]` : "",
    hasValidPosition ? position : "",
  ]
    ?.filter(Boolean)
    ?.join(" ");

  // Build inline styles for required parameters
  const buttonStyles = {
    fontSize: text_font_size ? `${text_font_size}px` : "24px",
    fontFamily: text_font_family || "Sora",
    fontWeight: text_font_weight || "700",
    lineHeight: text_line_height || "31px",
    textAlign: text_text_align || "center",
    color: text_color || "#1c233a",
    backgroundColor: hasValidBackgroundColor
      ? fill_background_color
      : "transparent",
    border: border_border || "1px solid #1c233a",
    borderRadius: border_border_radius || "24px",
  };

  // Safe click handler
  const handleClick = (event) => {
    if (disabled) return;
    if (typeof onClick === "function") {
      onClick(event);
    }
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={handleClick}
      style={buttonStyles}
      className={twMerge(
        buttonClasses({ variant, size }),
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children || text}
    </button>
  );
};

export default Button;
